import {
  createAccountId,
  createCompanyId,
  createFirmId,
  createLawyerEmailId,
  createLawyerId,
} from '@domain/value-objects';
import type { Unbrand } from '@shared/value-objects/brand';
import {
  ResponseMappingError,
  optVO,
  reqArray,
  reqRecord,
  reqVO,
} from '@shared/value-objects/parse-helpers';
import { createEmail, createPhone, createUrl } from '@shared/value-objects';
import { parseLawyerBulkSalesDecision } from '@domain/bulk-sales/parse';
import type { FirmAffiliatedLawyer, Lawyer, LawyerEmail } from './model';

/// API の生レスポンス (`unknown`) を検証して branded な `Lawyer` にする門番 (parse, don't validate / #471)。
/// 失敗方針: 必須 id・present かつ不正な任意フィールドは throw、任意の値なしは null、`raw` 値はログに載せない。
/// 所属は firm_id / company_id の排他 (ちょうど一方が非 null)。どちらも無い・両方ある応答は契約破れとして throw。

/// 排他検証前の生 DTO 表現。判別 union を落とした共通形で受ける。
type RawLawyer = Omit<Unbrand<FirmAffiliatedLawyer>, 'firm_id' | 'company_id'> & {
  firm_id?: string | null;
  company_id?: string | null;
  company_name?: string | null;
};

/// `lawyer_emails` 由来の登録メール配列 (`{ id, email }[]`) の門番。
/// 配列であること・各要素の id / email が妥当であることを要求する
/// (backend が常に供給する契約のため、欠落も contract 破れとして throw)。
export function parseLawyerEmails(raw: unknown, field: string): LawyerEmail[] {
  const entries = reqArray(raw, field);
  return entries.map((entry, index) => {
    const record = reqRecord(entry, `${field}[${index}]`) as {
      id?: unknown;
      email?: unknown;
    };
    return {
      id: reqVO(record.id, createLawyerEmailId, `${field}[${index}].id`),
      email: reqVO(record.email, createEmail, `${field}[${index}].email`),
    };
  });
}

export function parseLawyer(raw: unknown): Lawyer {
  reqRecord(raw, 'lawyer');
  const dto = raw as RawLawyer;
  const firmId = optVO(dto.firm_id, createFirmId, 'lawyer.firm_id');
  const companyId = optVO(dto.company_id, createCompanyId, 'lawyer.company_id');
  const common = {
    ...dto,
    id: reqVO(dto.id, createLawyerId, 'lawyer.id'),
    emails: parseLawyerEmails(dto.emails, 'lawyer.emails'),
    bulk_sales_decision: parseLawyerBulkSalesDecision(
      dto.bulk_sales_decision,
      'lawyer.bulk_sales_decision',
    ),
    direct_line: optVO(dto.direct_line, createPhone, 'lawyer.direct_line'),
    personal_site_url: optVO(dto.personal_site_url, createUrl, 'lawyer.personal_site_url'),
    assignee_account_id: optVO(
      dto.assignee_account_id,
      createAccountId,
      'lawyer.assignee_account_id',
    ),
  };
  if (firmId !== null && companyId === null) {
    return { ...common, firm_id: firmId, company_id: null };
  }
  if (firmId === null && companyId !== null) {
    return {
      ...common,
      firm_id: null,
      company_id: companyId,
      company_name: dto.company_name ?? null,
    };
  }
  throw new ResponseMappingError(
    'lawyer.affiliation',
    'exactly one of firm_id / company_id must be set',
  );
}
